import { observer } from "mobx-react-lite";

import * as S from "./style";
import type {
  ActionCandidate,
  InputCandidate,
} from "../../../../../../../../types/candidate";
import { JsonObject } from "./JsonObject/JsonObject";
import { ArrayObject } from "./ArrayObject/ArrayObject";
import type {
  InputDescription,
  ValueConfig,
} from "@smartsuite/automation-engine-api";
import { t } from "i18next";
import { useRootStore } from "../../../../../../../../stores/hooks";
import { isTruthy } from "remeda";

export const MAX_DEPTH = 10;

export const JsonEditor = observer<{
  input: InputCandidate;
  config?: ValueConfig;
  description: InputDescription;
  action?: ActionCandidate;
  onChange: (input: InputCandidate) => void;
  onlyStructure?: boolean;
  isError?: boolean;
}>(
  ({
    config,
    onChange,
    onlyStructure,
    input,
    description,
    action,
    isError,
  }) => {
    const store = useRootStore();
    const onClick = (): void => {
      onChange({ ...input, value: config });
    };

    const onRemove = (index: number, accept?: boolean): void => {
      if (!accept) {
        store.modal.openConfirmModal({
          type: "warning",
          title: t("confirm-modal.are-you-sure"),
          description: t("confirm-modal.want-delete-response"),
          positiveButtonText: t("confirm-modal.yes-delete-response"),
          negativeButtonText: t("confirm-modal.no-cancel"),
          onClose: () => {
            store.modal.closeConfirmModal();
          },
          onConfirm: () => {
            onRemove(index, true);
            store.modal.closeConfirmModal();
          },
        });
        return;
      }
      config?.values.splice(index, config.values.length);
      onChange({ ...input, value: config });
    };

    const onEdit = (index: number, accept?: boolean): void => {
      onChange({ ...input, value: config });
    };

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    const render = (value?: ValueConfig) => {
      if (value?.values.every((v) => v.value.oneofKind === "keyValue")) {
        return (
          <JsonObject
            description={description}
            onlyStructure={onlyStructure}
            onClick={onClick}
            depth={1}
            onRemove={onRemove}
            onEdit={onEdit}
            index={0}
            action={action}
            config={config}
          />
        );
      }
      if (value?.values.every((v) => v.value.oneofKind === "list")) {
        return value.values
          .map((v, index) => {
            return (
              v.value.oneofKind === "list" && (
                <ArrayObject
                  key={index}
                  description={description}
                  input={v.value.list}
                  onlyStructure={onlyStructure}
                  onClick={onClick}
                  depth={1}
                  onRemove={onRemove}
                  onEdit={onEdit}
                  index={0}
                  action={action}
                />
              )
            );
          })
          .filter(isTruthy);
      }
      return null;
    };

    return (
      <S.JsonEditor className={isError ? "is-error" : ""}>
        {render(config)}
      </S.JsonEditor>
    );
  },
);
JsonEditor.displayName = "JsonEditor";
